/**
 * Role-aware URL path helpers for the MCP Appointments API.
 * Shared by queries and mutations to avoid duplicating path construction.
 */

import { rolePath } from '@/utils/apiRole';
import { canUseAdminTenantScope } from '@/utils/authz';

const BASE_URL = '/mcp-appointments/api';

/** Role-aware base path for resources endpoints. */
export const roleResourcesBase = (role?: string) =>
  role === 'CUSTOMER'
    ? `${BASE_URL}/customer/resources`
    : `${BASE_URL}/${rolePath(role, 'resources')}`;

/**
 * Role-aware base path for appointments endpoints.
 * Customers use `/customer/appointments` (not staff `/client/appointments`).
 */
export const roleAppointmentsBase = (role?: string) => {
  if (canUseAdminTenantScope(role)) return `${BASE_URL}/appointments`;
  if (role === 'CUSTOMER') return `${BASE_URL}/customer/appointments`;
  return `${BASE_URL}/client/appointments`;
};

/** Customer-scoped reschedule requests list endpoint. */
export const customerRescheduleRequestsBase = () =>
  `${BASE_URL}/customer/appointment-reschedule-requests`;

/** Customer-scoped cancellation requests list endpoint. */
export const customerCancellationRequestsBase = () =>
  `${BASE_URL}/customer/appointment-cancellation-requests`;

/** Tenant-scoped reschedule requests list endpoint. */
export const clientRescheduleRequestsBase = () =>
  `${BASE_URL}/client/appointment-reschedule-requests`;

/** Role-aware base path for tenant reschedule request endpoints. */
export const roleRescheduleRequestsBase = (role?: string) => {
  if (canUseAdminTenantScope(role)) return `${BASE_URL}/appointments/appointment-reschedule-requests`;
  return clientRescheduleRequestsBase();
};

/** Tenant-scoped cancellation requests list endpoint. */
export const clientCancellationRequestsBase = () =>
  `${BASE_URL}/client/appointment-cancellation-requests`;

/** Role-aware base path for services endpoints. */
export const roleServicesBase = (role?: string) =>
  role === 'CUSTOMER'
    ? `${BASE_URL}/customer/services`
    : `${BASE_URL}/${rolePath(role, 'services')}`;

/** Role-aware base path for service-roles endpoints. */
export const roleServiceRolesBase = (role?: string) =>
  `${BASE_URL}/${rolePath(role, 'service-roles')}`;
